import Drawer from "../Drawer";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  COLUMN_GROUP_ORDER,
  DEFAULT_COLUMNS,
  GROUP_LABELS,
  cloneColumns,
} from "../../consts/productCatalog";

const groupColumns = (columns) => {
  const byGroup = {};
  for (const col of columns) {
    if (!byGroup[col.group]) byGroup[col.group] = [];
    byGroup[col.group].push(col);
  }
  return byGroup;
};

export const ProductCatalogColumnsSettings = ({
  isOpen,
  onClose,
  columns,
  defaultColumns = DEFAULT_COLUMNS,
  onChangeColumns,
  onSaveCurrentView,
}) => {
  const byGroup = groupColumns(columns);

  const handleToggle = (id) => {
    onChangeColumns((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (c.pinned === "left") return c;
        return { ...c, visible: !c.visible };
      })
    );
  };

  const handleDefault = () => {
    onChangeColumns(cloneColumns(defaultColumns));
  };

  const handleShowAll = () => {
    onChangeColumns((prev) => prev.map((c) => ({ ...c, visible: true })));
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Column Visibility"
      width="500px"
      footer={
        <div className="border-t border-gray-200 px-6 py-4 bg-white">
          <button
            type="button"
            onClick={onSaveCurrentView}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-primary shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <PlusIcon className="h-5 w-5" />
            Save Current View
          </button>
        </div>
      }
    >
      <div className="px-6 py-5 flex flex-col gap-5">
        <p className="text-sm text-gray-600">
          Show or hide columns in the catalog table.
        </p>
        <div className="flex flex-row gap-2">
          <button
            type="button"
            onClick={handleDefault}
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Default
          </button>
          <button
            type="button"
            onClick={handleShowAll}
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Show All
          </button>
        </div>

        <div className="flex flex-col gap-6 pb-2">
          {COLUMN_GROUP_ORDER.map((groupKey) => {
            const groupCols = byGroup[groupKey];
            if (!groupCols?.length) return null;
            return (
              <section key={groupKey} className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {GROUP_LABELS[groupKey] ?? groupKey}
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {groupCols.map((col) => {
                    const isPinned = col.pinned === "left";
                    return (
                    <li
                      key={col.id}
                      className="flex flex-row items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-gray-50"
                    >
                      <label
                        className={`flex flex-row items-center gap-3 min-w-0 flex-1 ${
                          isPinned ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={col.visible}
                          disabled={isPinned}
                          onChange={() => handleToggle(col.id)}
                          className="h-4 w-4 rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-70"
                        />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {col.label}
                        </span>
                      </label>
                      {isPinned && (
                        <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          pinned
                        </span>
                      )}
                    </li>
                  );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </Drawer>
  );
};
